// 16-PPH-EVIDENCE-R1 (2026-06-14, user request): Riwayat pembayaran PPh per
// invoice — daftar pembayaran (tanggal, NTPN, bukti setor, metode, bank,
// nominal alokasi, pencatat otomatis) + tautan bukti yang bisa dibuka kembali.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FileText, Pencil } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";

export function evidenceUrl(path) {
  if (!path) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // 2026-10-21: auth via cookie HttpOnly ens_token (migrasi cookie) — <a>/<img>
  // same-origin mengirim cookie otomatis; ?auth= dari localStorage tidak dipakai.
  return `${base}${path}`;
}

export default function PphPaymentHistoryDialog({ item, canManage, onClose, onEdit }) {
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/pph-payments", {
        params: { project_id: item.project_id, entry_id: item.entry_id },
      });
      setPayments(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Gagal memuat riwayat pembayaran.");
      setPayments([]);
    } finally { setLoading(false); }
  }, [item.project_id, item.entry_id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-xl bg-white border border-slate-300 shadow-xl max-h-[90vh] overflow-y-auto"
        data-testid="pph-history-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Riwayat Pembayaran PPh</div>
            <div className="text-sm font-bold text-[#0B1D3A]">{item.invoice_no || "Belum ada nomor invoice"}</div>
            <div className="text-[10px] text-slate-500">{item.project_name} · PPh {formatIDR(item.pph_amount)}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="pph-history-close" className="p-1 text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="px-4 py-3 space-y-2">
          {loading ? (
            <div className="p-4 text-center text-[11px] text-slate-400 italic">Memuat…</div>
          ) : !payments?.length ? (
            <div className="p-4 text-center text-[11px] text-slate-400" data-testid="pph-history-empty">
              {item.paid_to_state
                ? "Ditandai sudah dibayar (data lama) — Bukti belum dilengkapi. Tidak ada record pembayaran terinci."
                : "Belum ada pembayaran PPh untuk invoice ini."}
            </div>
          ) : payments.map((p) => {
            const myAlloc = (p.allocations || []).find((a) => a.project_id === item.project_id && a.entry_id === item.entry_id);
            return (
              <div key={p.id} className="border border-slate-200 p-3 space-y-1.5" data-testid={`pph-history-item-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-bold text-[#0B1D3A] tabular-nums">{formatIDR(myAlloc?.amount || 0)}</div>
                  <div className="text-[10px] text-slate-500 tabular-nums">{fmtDate(p.payment_date)}</div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-slate-600">
                  <div>NTPN: <span className="font-semibold">{p.ntpn || "—"}</span></div>
                  <div>Bukti Setor/Billing: <span className="font-semibold">{p.proof_number || "—"}</span></div>
                  <div>Metode: <span className="font-semibold">{p.method || "—"}</span></div>
                  <div>Bank: <span className="font-semibold">{p.bank || "—"}</span></div>
                </div>
                {p.note && <div className="text-[10px] text-slate-500 italic">{p.note}</div>}
                <div className="flex items-center gap-2 pt-0.5">
                  {p.evidence ? (
                    <a href={evidenceUrl(p.evidence.url)} target="_blank" rel="noreferrer"
                      data-testid={`pph-evidence-link-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase font-semibold border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white">
                      <FileText size={10} /> Buka Bukti ({p.evidence.filename || "file"})
                    </a>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border bg-amber-50 text-amber-700 border-amber-300"
                      data-testid={`pph-evidence-missing-${p.id}`}>
                      Bukti belum dilengkapi
                    </span>
                  )}
                  {canManage && (
                    <button type="button" onClick={() => onEdit(p)} data-testid={`pph-edit-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
                      <Pencil size={10} /> Koreksi
                    </button>
                  )}
                </div>
                <div className="text-[9px] text-slate-400">
                  Dicatat oleh {p.created_by_name || "—"} · {p.created_at ? new Date(p.created_at).toLocaleString("id-ID") : "—"}
                  {p.updated_by_name ? ` · Koreksi terakhir oleh ${p.updated_by_name}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
